import { useMemo } from "react";

import { type TopLevelDataObj } from "@hsl/fund-explorer/types";

import { type TabConfig } from "../../../config/types";
import useFundData from "../../../hooks/useFundData";
import useShareClasses from "../../../hooks/useShareClasses";
import { type Props as FundCardTableProps } from "../FundCardTable";
import Table from "../Table";
import GroupedTabView from "./GroupedTabView";
import View from "./View";

const TabView = ({
    tab,
    config,
    shareClasses,
    subTabs,
    spacedTabs,
    footnote,
}: FundCardTableProps) => {
    const shareClassData = useShareClasses(shareClasses);
    const fundData = useFundData(shareClassData[0]?.parentId ?? "");
    const { priceType } = fundData?.data?.data ?? {};
    const fundDeps = [["priceType", priceType]]
        .filter(([_, val]) => Boolean(val))
        .reduce((acc, [key, val]) => ({ ...acc, [key as string]: val }), {});
    const tabSCMap = subTabs
        ?.map((subTab) => ({
            tab: subTab,
            data: shareClassData.filter((sc) =>
                (subTab.restrictions ?? []).every(
                    (r) =>
                        r.type !== "shareclass" ||
                        r.value.includes(sc.data[r.prop] as string),
                ),
            ),
        }))
        .filter((tabSC) => tabSC.data.length > 0);

    const Content = useMemo(() => {
        if (Array.isArray(subTabs) && subTabs.length > 0) {
            return tab.groups
                ? subTabs.map((subTab) => (
                      <GroupedTabView
                          key={`${tab.key}-${subTab.key}`}
                          groups={getTabGroups(tab, shareClassData)}
                          tab={subTab}
                          config={config}
                          shareClasses={shareClasses}
                          spacedTabs={spacedTabs}
                          footnote={footnote}
                          groupShareClasses
                      />
                  ))
                : (tabSCMap ?? []).map(({ tab: subTab, data }) => {
                      return (
                          <Table
                              key={subTab.key}
                              tab={subTab}
                              config={config}
                              data={data}
                              fundDeps={fundDeps}
                          />
                      );
                  });
        } else {
            return (
                <Table
                    key={shareClassData[0]?.localId}
                    tab={tab}
                    config={config}
                    data={shareClassData ?? []}
                    containerClassName="mt-2 sm:mt-4"
                    fundDeps={fundDeps}
                />
            );
        }
    }, [
        subTabs,
        tab,
        tabSCMap,
        shareClassData,
        config,
        shareClasses,
        spacedTabs,
        footnote,
        fundDeps,
    ]);

    return (
        <View
            content={Content}
            tabs={tabSCMap?.map((tabSC) => tabSC.tab)}
            config={config.tabs.find((t) => t.key === tab.key) as TabConfig}
            spacedTabs={spacedTabs}
            footnote={footnote}
        />
    );
};

export default TabView;

function getTabGroups(
    tab: FundCardTableProps["tab"],
    shareClasses: TopLevelDataObj[],
): FundCardTableProps["groups"] {
    return tab.groups!.reduce(
        (acc, group) => ({
            ...acc,
            [`${group.key}@${group.label}`]: shareClasses.filter((sc) =>
                group.restrictions.every(
                    (r) =>
                        r.type !== "shareclass" ||
                        r.value.includes(sc.data[r.prop] as string),
                ),
            ),
        }),
        {},
    );
}
